import { ShareBetResponseModel } from 'models';
import React, { useEffect, useMemo, useState } from 'react';
import ShareBetSingle from './ShareBetSingle';
import ShareBetCombo from './ShareBetCombo';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import { platformStates } from 'redux/platformSlice';
import Typo from 'consts/typography';
import ConditionButton from 'components/Commons/ConditionButton';
import { showSuccessModal } from 'components/Modals/CommonModals';
import oddsApi from 'api/oddsApi';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import { PLACE_BET_AUDIO } from 'consts/app';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useThemes from 'hooks/useThemes';
import { isValidAmount } from 'features/home/components/Modals/WithdrawModal';
import { clearFollowBet, selectFollowBet, setFollowBet } from 'redux/chatSlice';
import { Modal } from 'antd';
import CloseIcon from 'assets/icons/CloseIcon';
import { getPossibleWin, getPossibleWinComboWithAmount } from 'utils/betUtils';
import ShareBetTeaser from './ShareBetTeaser';
import BigNumber from 'bignumber.js';

interface ShareBetMessageProps {
  message: ShareBetResponseModel;
  myMessage?: boolean;
}
const aud = new Audio(PLACE_BET_AUDIO);
const ShareBetMessage: React.FunctionComponent<ShareBetMessageProps> = ({
  message,
  myMessage = false,
}) => {
  const t = useAppTranslation();
  const platform = useAppSelector(platformStates);
  const { isDesktop, isLargeDesktop } = platform;
  const dispatch = useAppDispatch();

  const handleFollowBet = () => {
    dispatch(setFollowBet(message));
  };

  return (
    <div className="share-bet__message">
      <span>{t('sharebet.message')}</span>
      <div className="share-bet__content">
        {message.type === 'SINGLE' && <ShareBetSingle message={message} />}
        {message.type === 'COMBO' && <ShareBetCombo message={message} />}
        {message.type === 'TEASER' && <ShareBetTeaser message={message} />}
      </div>
      <div className="share-bet__footer">
        {!myMessage && message.status === 'WAITING' && (
          <ConditionButton
            onClick={handleFollowBet}
            className={`follow-bet__btn ${isDesktop || isLargeDesktop ? Typo.p4 : Typo.p6}`}
            content={t('sharebet.follow-btn')}
            testerCheck={true}
          />
        )}
      </div>

      <FollowBetModal />
    </div>
  );
};

export default ShareBetMessage;

interface FollowBetModalProps {}

export const FollowBetModal: React.FunctionComponent<FollowBetModalProps> = ({}) => {
  const t = useAppTranslation();
  const selectMessage = useAppSelector(selectFollowBet);
  const dispatch = useAppDispatch();
  const handleResponseError = useHandleResponseError();
  const { getWallet, currentWallet } = useHandleGetWallets();
  const { currentTheme } = useThemes();
  const [amountToBet, setAmountToBet] = useState<string>('');
  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{3,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;
    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmountToBet(value);
    }
  };

  useEffect(() => {
    if (selectMessage) setAmountToBet(selectMessage?.amount.toString());
  }, [selectMessage]);

  const onFollowBet = async () => {
    const { ok, error } = await oddsApi.copyBets({
      betId: selectMessage?.id || 0,
      amount: Number(amountToBet),
    });
    const onOk = () => {
      dispatch(clearFollowBet());
    };
    if (ok) {
      aud.play();
      getWallet();
      showSuccessModal({
        title: t('bet.success.title'),
        content: t('bet.success.message'),
        onOk: onOk,
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };
  const onCloseModal = () => {
    dispatch(clearFollowBet());
  };

  const visible = useMemo(() => {
    return selectMessage ? true : false;
  }, [selectMessage]);

  const getPossibleWinAmount = useMemo(() => {
    if (selectMessage) {
      if (selectMessage?.type === 'SINGLE') {
        if (selectMessage.betDetails[0].type === 'BET_FUTURE') {
          return getPossibleWin(
            Number(amountToBet),
            selectMessage.betDetails[0].futureOdd?.decimalPrice,
          );
        } else {
          return getPossibleWin(
            Number(amountToBet),
            selectMessage.betDetails[0].market.decimalPrice,
          );
        }
      } else if (selectMessage?.type === 'COMBO') {
        return getPossibleWinComboWithAmount(selectMessage, Number(amountToBet));
      } else {
        const winAmount = new BigNumber(selectMessage.amount)
          .times(new BigNumber(selectMessage.teaserPoint?.decimalPrice))
          .minus(new BigNumber(selectMessage.amount));
        return winAmount.toFixed(2);
      }
    }
    return;
  }, [selectMessage?.id, amountToBet]);
  return (
    <Modal
      title={t('sharebet.popup.content')}
      visible={visible}
      className="common-modal withdraw-modal"
      onCancel={onCloseModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={600}
    >
      <div className="follow-form">
        <div className="w-full follow-form-container">
          <p className={` font-medium primary-text-color `}>Amount: </p>
          <input
            className="input-modal w-full "
            type="text"
            name="amount"
            id="amount"
            autoComplete="off"
            value={amountToBet}
            onChange={handleAmountChange}
          />
        </div>
        <div className="w-full follow-form-container">
          <p className={` font-medium primary-text-color `}>Possible Win</p>
          <p className="follow-form-posibleWin">{getPossibleWinAmount}$</p>
        </div>
        <div className="flex-row items-center follow-form-btn w-full">
          <button onClick={onFollowBet}>{t('bet.placeBet')}</button>
          <button onClick={onCloseModal}>{t('sharebet.popup.button-cancel')}</button>
        </div>
      </div>
    </Modal>
  );
};
